/* eslint-disable no-console */
interface UserData {
	id: string;
	name?: string;
	profile?: {
		name?: string;
	};
	emails?: Array<{ address: string; verified?: boolean }>;
	phones?: Array<{ number: string; verified?: boolean }>;
	createdAt?: string;
	groups?: Array<{ id: string; relationship: string }>;
	internal?: boolean;
}

import { logger } from "../logger";
import { trackEvent } from "./track-event";

/**
 * Handle login analytics and tracking after successful phone OTP authentication
 * Optimized for phone-based authentication flow
 */
export async function trackLogin({
	userData,
	loginType = "phone_otp",
}: {
	userData: UserData;
	loginType: string;
}): Promise<void> {
	try {
		// Validate userData
		if (!userData || !userData.id) {
			logger.error("Invalid userData provided to trackLogin", { userData, loginType });
			return;
		}

		// Validate userData.id is a string
		if (typeof userData.id !== "string" || userData.id.trim() === "") {
			logger.error("Invalid userData.id provided to trackLogin", {
				userId: userData.id,
				loginType,
			});
			return;
		}
		// Use the new Web Worker bridge if available
		if (typeof window !== "undefined" && window.trackUserLogin) {
			const primaryPhone = userData.phones?.[0];
			const primaryEmail = userData.emails?.[0];

			window.trackUserLogin(userData.id, loginType, {
				name: userData.name || userData.profile?.name,
				email: primaryEmail?.address,
				phone: primaryPhone?.number,
				phoneVerified: primaryPhone?.verified,
				emailVerified: primaryEmail?.verified,
				groupCount: userData.groups?.length || 0,
				relationship: Array.isArray(userData.groups) ? userData.groups[0]?.relationship : undefined,
			});

			// Track phone OTP login event with additional context
			trackEvent({
				google: {
					event: "login",
					options: {
						cUserId: userData.id,
						loginMethod: loginType,
						phoneVerified: primaryPhone?.verified || false,
						hasEmail: !!primaryEmail?.address,
						hasGroups: (userData.groups?.length || 0) > 0,
					},
				},
			});

			// Track new account if user was created recently (within 1 minute)
			if (loginType !== "session_resume" && userData.createdAt) {
				try {
					const createdAt = new Date(userData.createdAt);

					// Validate that the date is valid
					if (isNaN(createdAt.getTime())) {
						logger.warn("Invalid createdAt date format", { createdAt: userData.createdAt });
					} else {
						const oneMinuteAgo = new Date(Date.now() - 60000);

						if (createdAt > oneMinuteAgo) {
							trackEvent({
								google: {
									event: "new-account",
									options: {
										registrationMethod: loginType,
										phoneNumber: primaryPhone?.number,
										hasEmail: !!primaryEmail?.address,
									},
								},
							});
						}
					}
				} catch (dateError) {
					logger.error("Error parsing createdAt date", {
						createdAt: userData.createdAt,
						error: dateError,
					});
				}
			}

			return;
		}

		// Fallback implementation (for development or when worker isn't available)
		// Google Analytics
		if (window.gtag && process.env.NEXT_PUBLIC_GA_TRACKING_ID) {
			try {
				window.gtag("config", process.env.NEXT_PUBLIC_GA_TRACKING_ID, {
					user_id: userData.id,
				});

				window.gtag("get", process.env.NEXT_PUBLIC_GA_TRACKING_ID, "client_id", (clientId: string) => {
					try {
						if (!clientId) {
							const { userAgent } = window.navigator;
							logger.debug("Google Analytics failed - Login tracking ", {
								type: loginType,
								user: userData,
								userAgent,
							});
						} else {
							// You would make an API call to store this insight
							fetch("/api/user/insights", {
								method: "POST",
								headers: { "Content-Type": "application/json" },
								body: JSON.stringify({ account: "google", id: clientId }),
							}).catch(e => logger.debug("API call failed ", e));
						}
					} catch (gtagCallbackError) {
						logger.debug("Error in Google Analytics callback ", {
							error: gtagCallbackError,
							clientId,
						});
					}
				});

				trackEvent({ google: { event: "login", options: { cUserId: userData.id } } });
			} catch (gtagError) {
				logger.debug("Error in Google Analytics tracking ", {
					error: gtagError,
					userId: userData.id,
				});
			}
		}

		// Matomo Analytics
		if (window._paq) {
			try {
				window._paq.push(["setUserId", userData.id]);
			} catch (matomoError) {
				logger.debug("Error in Matomo tracking ", {
					error: matomoError,
					userId: userData.id,
				});
			}
		}

		// Mixpanel - Enhanced for phone OTP authentication
		if (window.mixpanel) {
			try {
				window.mixpanel.alias?.(userData.id);
				window.mixpanel.identify?.(userData.id);

				// Set user properties
				const userProperties: Record<string, unknown> = {
					loginMethod: loginType,
					lastLoginAt: new Date().toISOString(),
				};

				if (userData.profile?.name || userData.name) {
					userProperties.name = userData.profile?.name || userData.name;
				}

				if (userData.emails?.length) {
					userProperties.Email = userData.emails[0]?.address;
					userProperties.emails = userData.emails.map(e => e.address);
					userProperties.emailVerified = userData.emails[0]?.verified || false;
					userProperties.hasEmail = true;
				} else {
					userProperties.hasEmail = false;
				}

				if (userData.phones?.length) {
					userProperties.Phone = userData.phones[0]?.number;
					userProperties.phones = userData.phones.map(e => e.number);
					userProperties.phoneVerified = userData.phones[0]?.verified || false;
					userProperties.hasPhone = true;
				} else {
					userProperties.hasPhone = false;
				}

				if (userData.groups?.length) {
					userProperties.groupCount = userData.groups.length;
					userProperties.primaryRelationship = Array.isArray(userData.groups)
						? userData.groups[0]?.relationship
						: undefined;
					userProperties.relationships = userData.groups.map(g => g.relationship);
				}

				if (window.mixpanel.people) {
					window.mixpanel.people.set?.(userProperties);
				}

				// Track login event with context
				window.mixpanel.track?.("User Login", {
					method: loginType,
					phoneVerified: userData.phones?.[0]?.verified || false,
					emailVerified: userData.emails?.[0]?.verified || false,
					hasGroups: (userData.groups?.length || 0) > 0,
					groupCount: userData.groups?.length || 0,
				});
			} catch (mixpanelError) {
				logger.debug("Error in Mixpanel tracking ", {
					error: mixpanelError,
					userId: userData.id,
				});
			}
		}

		// Userback
		const _userbackTimeout = setTimeout(() => {
			try {
				if (window.Userback) {
					window.Userback.identify(userData.id, {
						name: userData.name || userData.profile?.name,
						email: userData.emails?.[0]?.address,
					});
				}
			} catch (userbackError) {
				logger.debug("Error in Userback identification ", {
					error: userbackError,
					userId: userData.id,
				});
			}
		}, 3000);

		// Store timeout handle for potential cleanup (optional)
		// Note: In a real application, you might want to return this handle
		// or store it in a cleanup registry for proper memory management

		// Crisp
		if (window.$crisp) {
			try {
				// You would need to implement sendUserData function
				// require("/common/utils/client/crisp").sendUserData();
			} catch (crispError) {
				console.error("Error in Crisp integration", {
					error: crispError,
					userId: userData.id,
				});
			}
		}

		// Google Tag Manager
		trackEvent({
			google: { event: "login", options: { cUserId: userData.id } },
			facebook: { event: "login" },
			mixpanel: { event: "login" },
			matomo: { event: "login" },
		});

		// Track new account if user was created recently (within 1 minute)
		if (loginType !== "session_resume" && userData.createdAt) {
			const createdAt = new Date(userData.createdAt);
			const oneMinuteAgo = new Date(Date.now() - 60000);

			if (createdAt > oneMinuteAgo) {
				trackEvent({ google: { event: "new-account" } });
			}
		}
	} catch (error) {
		logger.debug("Login analytics error " + (error as Error).toString(), error);
	}
}
